/**
 * Ni² Structured Answer — presentation-layer component that parses
 * the model's raw markdown-ish stream into the four sections the
 * demo brief now requires:
 *
 *   • Recommendation (always expanded, visually emphasised)
 *   • Supporting evidence / scope-specific layered sections (collapsible)
 *   • Source Summary (compact visual card with counts)
 *   • Suggested Follow-up Actions (derived from tool calls actually made)
 *
 * The parser is intentionally forgiving — the model emits `**Bold**`
 * markdown headers ("**Recommendation:**", "**Supporting evidence**",
 * etc.). We split on those, then progressively enrich each block with:
 *   • inline provenance chips generated from `[VS-RAO-001, REF-002]`
 *     citation tokens rendered right after the sentence they belong to.
 *   • an accordion around anything below the recommendation.
 *
 * This component does NOT touch retrieval, tool routing, ownership,
 * or scope selection — those are backend concerns and remain untouched.
 */
import { useMemo, useState, useEffect } from "react";
import {
  ChevronDown,
  Brain,
  Building2,
  BookOpen,
  Layers,
  Scan,
  Lightbulb,
  Quote,
  FileText,
  Sparkles,
  ClipboardList,
  ArrowRight,
} from "lucide-react";

// ---------- Citation → provenance chip mapping -----------------------
// The model emits repository IDs in square brackets, e.g.
//   "[VS-RAO-001, REF-001]"
// We turn each ID into a click-through chip. Kinds line up with the
// existing ProvenanceTag colour system: blue = imaging/literature,
// amber = personal reflection / M&M / operative / case memory.
const ID_PATTERNS = [
  { rx: /^VS-[A-Z]+-\d+$/i,   kind: "amber", label: "Case",       category: "case" },
  { rx: /^CASE-\d+$/i,        kind: "amber", label: "Case",       category: "case" },
  { rx: /^REF-\d+$/i,         kind: "amber", label: "Reflection", category: "reflection" },
  { rx: /^PEARL-\d+$/i,       kind: "amber", label: "Pearl",      category: "pearl" },
  { rx: /^MM(-[A-Z0-9]+)?-\d+$/i, kind: "amber", label: "M&M",    category: "mm" },
  { rx: /^IMG-\d+$/i,         kind: "blue",  label: "MRI",        category: "imaging" },
  { rx: /^LIT-\d+$/i,         kind: "blue",  label: "Paper",      category: "literature" },
  { rx: /^TLE-.*$/i,          kind: "amber", label: "Timeline",   category: "timeline" },
  { rx: /^COLLAB(-[A-Z0-9]+)?-\d+$/i, kind: "amber", label: "Note", category: "collaboration" },
];

function classifyId(id) {
  const found = ID_PATTERNS.find((p) => p.rx.test(id));
  if (found) return { ref: id, ...found };
  // Unknown IDs still render as a neutral chip so the demo never eats provenance.
  return { ref: id, kind: "amber", label: "Ref", category: "unknown" };
}

// Rich text renderer for a piece of a bullet / sentence.
// Splits on `[A, B, C]` citation groups and turns each ID into a chip.
// Chips are emitted INLINE next to the text so provenance sits with the
// statement it supports (per the brief).
function renderInlineWithChips(text, onOpenChip) {
  if (!text) return null;
  const nodes = [];
  const rx = /\[([A-Z0-9,\-\s\/]+)\]/g;
  let last = 0;
  let m;
  let key = 0;
  const seenInRun = new Set();
  const emitText = (t) => {
    if (!t) return;
    // Trim double spaces around chips.
    nodes.push(<span key={`t-${key++}`}>{t}</span>);
  };
  while ((m = rx.exec(text)) !== null) {
    emitText(text.slice(last, m.index));
    const ids = m[1]
      .split(/[,\/]/)
      .map((s) => s.trim())
      .filter(Boolean);
    ids.forEach((id, i) => {
      if (seenInRun.has(id)) return; // brief: never repeat the same chip within one paragraph
      seenInRun.add(id);
      const c = classifyId(id);
      const isBlue = c.kind === "blue";
      nodes.push(
        <button
          key={`c-${key++}`}
          type="button"
          onClick={() => onOpenChip && onOpenChip({ id: c.ref, category: c.category })}
          data-testid={`ni2-inline-chip-${c.ref}`}
          className={"nn-prov mx-0.5 align-baseline " + (isBlue ? "nn-prov-blue" : "")}
          title={c.ref}
        >
          <span className="uppercase tracking-[0.06em]">{c.label}</span>
          <span className="text-[hsl(var(--nn-ink-faint))]">·</span>
          <span>{c.ref}</span>
        </button>
      );
    });
    last = m.index + m[0].length;
  }
  emitText(text.slice(last));
  return nodes;
}

// ---------- Parser ---------------------------------------------------
// The model output uses `**Header**` markdown for section labels. We
// split by top-level headers we care about, tolerating case + colons.
const KNOWN_HEADERS = [
  { key: "recommendation",  match: /^\**\s*recommendation\s*:?\**/i },
  { key: "keyTakeaways",    match: /^\**\s*key\s+take\s?aways?\s*:?\**/i },
  { key: "evidence",        match: /^\**\s*supporting\s+evidence\s*:?\**/i },
  { key: "myMemory",        match: /^\**\s*my\s+memory\s*:?\**/i },
  { key: "department",      match: /^\**\s*department\s*:?\**/i },
  { key: "literature",      match: /^\**\s*literature\s*:?\**/i },
  { key: "synthesis",       match: /^\**\s*synthesis\s*:?\**/i },
  { key: "sourceSummary",   match: /^\**\s*source\s+summary\s*:?\**/i },
  { key: "limitations",     match: /^\**\s*limitations?\s*:?\**/i },
];

function stripBold(t) { return (t || "").replace(/\*\*/g, "").trim(); }

function parseAnswer(raw) {
  const src = (raw || "").trim();
  if (!src) return { recommendation: "", sections: [] };

  // Normalise line endings and drop stray markdown fences.
  const lines = src.replace(/\r\n?/g, "\n").split("\n");

  let currentKey = "preamble";
  const buckets = { preamble: [] };
  const orderedKeys = ["preamble"];

  for (const rawLine of lines) {
    const line = rawLine;
    // Detect header: either the ENTIRE line is a header, OR the header
    // appears at the start followed by inline content (Recommendation:
    // is nearly always followed by prose on the same line).
    let matched = null;
    let inlineTail = "";
    const stripped = line.trim();
    if (stripped) {
      const raw2 = stripped.replace(/^[-•·]\s+/, "");
      for (const h of KNOWN_HEADERS) {
        const m = raw2.match(h.match);
        if (m && (m.index === 0 || (m.index !== undefined && m.index < 3))) {
          // Header only counts if the header token appears at the start
          // of the line (allowing "**").
          matched = h;
          inlineTail = raw2.slice(m[0].length).replace(/^[:\-—\s]+/, "");
          break;
        }
      }
    }
    if (matched) {
      currentKey = matched.key;
      if (!buckets[currentKey]) {
        buckets[currentKey] = [];
        orderedKeys.push(currentKey);
      }
      if (inlineTail) buckets[currentKey].push(inlineTail);
    } else {
      buckets[currentKey].push(line);
    }
  }

  // Split each bucket's body into bullets (`•`, `-`, `*` markers) and
  // clean whitespace. Blank lines between bullets are dropped.
  const toBullets = (arr) => {
    const joined = arr.join("\n");
    // First break on lines starting with a bullet marker.
    const parts = joined
      .split(/\n(?=\s*[•\-*]\s+)/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length > 1) {
      return parts.map((p) => stripBold(p.replace(/^[•\-*]\s+/, "")));
    }
    // Fallback: single paragraph → one implicit bullet.
    return [stripBold(joined)];
  };

  const recommendation = stripBold((buckets.recommendation || []).join(" "));

  const sections = [];
  // Evidence-like sections in the order they appear.
  for (const k of orderedKeys) {
    if (k === "preamble" || k === "recommendation" || k === "sourceSummary") continue;
    const bullets = toBullets(buckets[k] || []).filter((b) => b && b.length > 1);
    if (!bullets.length) continue;
    sections.push({ key: k, bullets });
  }
  const sourceSummary = stripBold((buckets.sourceSummary || []).join(" "));

  return {
    recommendation,
    sections,
    sourceSummary,
    hasContent: !!recommendation || sections.length > 0,
  };
}

// ---------- Layered source-summary counts ----------------------------
// We build the source card from BOTH the parsed "Source summary" line
// AND the actual tool calls the executor emitted, whichever is richer.
function deriveSourceLayers({ tools, parsed, scope }) {
  const t = (tools || []).filter((x) => x && !x.denied);
  const has = (name) => t.some((x) => x.name === name);
  const layers = [];

  if (
    scope === "personal" ||
    scope === "synthesize" ||
    has("get_personal_cases") ||
    has("get_personal_reflections") ||
    has("get_personal_operative_pearls") ||
    has("get_personal_imaging")
  ) {
    layers.push({ key: "personal", label: "My Memory", Icon: Brain });
  }
  if (
    scope === "department" ||
    scope === "synthesize" ||
    has("search_department_memory") ||
    has("get_mm_record_by_id")
  ) {
    layers.push({ key: "department", label: "Department", Icon: Building2 });
  }
  if (
    scope === "literature" ||
    scope === "synthesize" ||
    has("search_literature") ||
    has("get_literature_by_ids")
  ) {
    layers.push({ key: "literature", label: "Literature", Icon: BookOpen });
  }

  // Count records referenced in the answer's citation IDs (simple
  // heuristic — this is a demo card, not a database report).
  const idsInAnswer = new Set();
  const rx = /\[([A-Z0-9,\-\s\/]+)\]/g;
  const src = (parsed.recommendation || "") + "\n" + parsed.sections.map((s) => s.bullets.join(" ")).join("\n");
  let m;
  while ((m = rx.exec(src)) !== null) {
    m[1].split(/[,\/]/).map((s) => s.trim()).filter(Boolean).forEach((id) => idsInAnswer.add(id));
  }
  const countByPrefix = (fn) => Array.from(idsInAnswer).filter(fn).length;

  return layers.map((l) => {
    let count = 0;
    if (l.key === "personal") count = countByPrefix((id) => /^(REF|PEARL|VS-RAO|IMG)-/i.test(id));
    if (l.key === "department") count = countByPrefix((id) => /^(MM|CASE|VS-(?!RAO))/i.test(id));
    if (l.key === "literature") count = countByPrefix((id) => /^LIT-/i.test(id));
    return { ...l, count };
  });
}

// ---------- Follow-up actions ---------------------------------------
// Only include actions that are actually supported by the retrieved
// records — so `Open Imaging` shows only when an IMG-* citation exists,
// `Generate M&M Discussion` shows only when a personal case is on-scene,
// etc. Selection uses the tool-call metadata + citation IDs.
function deriveFollowUps({ parsed, tools, scope }) {
  const actions = [];
  const t = (tools || []).filter((x) => x && !x.denied);
  const has = (name) => t.some((x) => x.name === name);

  const idsInAnswer = new Set();
  const rx = /\[([A-Z0-9,\-\s\/]+)\]/g;
  const src = (parsed.recommendation || "") + "\n" + parsed.sections.map((s) => s.bullets.join(" ")).join("\n");
  let m;
  while ((m = rx.exec(src)) !== null) {
    m[1].split(/[,\/]/).map((s) => s.trim()).filter(Boolean).forEach((id) => idsInAnswer.add(id));
  }
  const idsArr = Array.from(idsInAnswer);
  const first = (test) => idsArr.find(test);

  const refId = first((id) => /^REF-/i.test(id));
  const imgId = first((id) => /^IMG-/i.test(id));
  const caseId = first((id) => /^VS-/i.test(id));
  const mmId = first((id) => /^MM(-|$)/i.test(id));
  const litId = first((id) => /^LIT-/i.test(id));

  if (refId) actions.push({ id: "open-reflection", label: "Open Reflection", Icon: Quote, target: refId });
  if (imgId) actions.push({ id: "open-imaging", label: "Open Imaging", Icon: Scan, target: imgId });
  if (caseId) actions.push({ id: "review-cases", label: "Review Similar Cases", Icon: FileText, target: caseId });
  if (mmId) actions.push({ id: "open-mm", label: "Open M&M Discussion", Icon: ClipboardList, target: mmId });
  if (litId) actions.push({ id: "open-lit", label: "Open Paper", Icon: BookOpen, target: litId });

  // Compare-with actions — only offer switching layers if the current
  // scope isn't already showing that layer.
  if (scope === "personal") {
    if (has("get_personal_cases") || has("get_personal_reflections")) {
      actions.push({ id: "compare-department", label: "Compare with Department", Icon: Building2, target: "scope:department" });
      actions.push({ id: "compare-literature", label: "Compare with Literature", Icon: BookOpen, target: "scope:literature" });
    }
  } else if (scope === "department") {
    actions.push({ id: "compare-mine", label: "Compare with My Memory", Icon: Brain, target: "scope:personal" });
    actions.push({ id: "compare-literature", label: "Compare with Literature", Icon: BookOpen, target: "scope:literature" });
  } else if (scope === "literature") {
    actions.push({ id: "compare-mine", label: "Compare with My Memory", Icon: Brain, target: "scope:personal" });
    actions.push({ id: "compare-department", label: "Compare with Department", Icon: Building2, target: "scope:department" });
  }

  // Generate M&M Discussion — only if a personal case with a clinical
  // twist (MM-* record OR M&M scope hint) is present.
  if (caseId && (mmId || has("get_mm_record_by_id"))) {
    actions.push({ id: "generate-mm", label: "Generate M&M Discussion", Icon: Sparkles, target: caseId });
  }

  // De-dupe by id and cap at 5 to avoid clutter.
  const seen = new Set();
  return actions.filter((a) => (seen.has(a.id) ? false : seen.add(a.id))).slice(0, 5);
}

// ---------- Section label + icon meta -------------------------------
const SECTION_META = {
  keyTakeaways: { label: "Key Takeaways",      Icon: Sparkles },
  evidence:   { label: "Supporting Evidence",    Icon: Sparkles },
  myMemory:   { label: "My Memory",              Icon: Brain },
  department: { label: "Department",             Icon: Building2 },
  literature: { label: "Literature",             Icon: BookOpen },
  synthesis:  { label: "Synthesis",              Icon: Layers },
  limitations:{ label: "Limitations",            Icon: Lightbulb },
};

// ---------- The component -------------------------------------------
export default function Ni2StructuredAnswer({
  streamText,
  streaming,
  tools = [],
  scope = "personal",
  demoMode = true,
  onOpenChip,
  onFollowUp,
  turnKey,
  synthesisStarted = false,
}) {
  const parsed = useMemo(() => parseAnswer(streamText), [streamText]);
  const layers = useMemo(() => deriveSourceLayers({ tools, parsed, scope }), [tools, parsed, scope]);
  const followUps = useMemo(() => deriveFollowUps({ parsed, tools, scope }), [parsed, tools, scope]);

  // Collapse state — remembered ONLY for the lifetime of this turn.
  // First evidence-like section starts expanded; others collapsed.
  const [openState, setOpenState] = useState({});
  useEffect(() => {
    if (!parsed.sections.length) return;
    setOpenState((prev) => {
      const next = { ...prev };
      let firstNonTakeawaysAssigned = false;
      parsed.sections.forEach((s) => {
        if (s.key in next) return;
        if (s.key === "keyTakeaways") {
          next[s.key] = true;
        } else if (!firstNonTakeawaysAssigned) {
          next[s.key] = true;
          firstNonTakeawaysAssigned = true;
        } else {
          next[s.key] = false;
        }
      });
      return next;
    });
  }, [parsed.sections.length, turnKey]);
  const toggle = (k) => setOpenState((p) => ({ ...p, [k]: !p[k] }));

  // While streaming and before the model has emitted the Recommendation
  // header, fall back to the plain-text stream so users still see live
  // tokens arriving. As soon as parsing succeeds we swap to structured.
  const isStructuredReady = parsed.hasContent && !!parsed.recommendation;
  if (!isStructuredReady) {
    // If the retrieval stages are still visible above, don't show a
    // duplicate ellipsis here — the progress rail is our indicator.
    if (streaming && !streamText) {
      return null;
    }
    return (
      <div className="whitespace-pre-wrap text-[13.5px] leading-[22px] text-[hsl(var(--nn-ink))]" data-testid="ni2-live-text">
        {streamText || (streaming ? "…" : "")}
        {streaming && (
          <span className="ml-0.5 inline-block h-3 w-1 -translate-y-[1px] animate-pulse bg-[hsl(var(--nn-blue))]" />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="ni2-structured-answer">
      {/* Recommendation — always expanded, visually emphasised. */}
      <div
        className="rounded-xl border px-3.5 py-3"
        style={{
          background: "linear-gradient(180deg, rgba(160,200,255,0.10), rgba(120,165,240,0.02))",
          borderColor: "rgba(160,200,255,0.32)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12)",
        }}
        data-testid="ni2-recommendation"
      >
        <div className="mb-1 flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--nn-blue))]">
          <Sparkles className="h-3 w-3" strokeWidth={2} />
          Recommendation
        </div>
        <div className="text-[15px] font-medium leading-[22px] text-[hsl(var(--nn-ink))]">
          {renderInlineWithChips(parsed.recommendation, onOpenChip)}
          {streaming && (
            <span className="ml-0.5 inline-block h-3 w-1 -translate-y-[1px] animate-pulse bg-[hsl(var(--nn-blue))]" />
          )}
        </div>
      </div>

      {/* Collapsible evidence sections. */}
      {parsed.sections.length > 0 && (
        <div className="flex flex-col gap-1.5" data-testid="ni2-evidence-sections">
          {parsed.sections.map((section) => {
            const meta = SECTION_META[section.key] || {
              label: section.key,
              Icon: Sparkles,
            };
            const isOpen = openState[section.key];
            const isLimit = section.key === "limitations";
            return (
              <div
                key={section.key}
                className="rounded-xl border border-white/[0.08] bg-white/[0.02]"
                data-testid={`ni2-section-${section.key}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(section.key)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
                  data-testid={`ni2-section-toggle-${section.key}`}
                  aria-expanded={isOpen}
                >
                  <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--nn-ink))]">
                    <meta.Icon
                      className="h-3 w-3"
                      strokeWidth={2}
                      style={{ color: isLimit ? "hsl(var(--nn-warn))" : "hsl(var(--nn-blue))" }}
                    />
                    {meta.label}
                    <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[9.5px] tracking-normal text-[hsl(var(--nn-ink-faint))]">
                      {section.bullets.length}
                    </span>
                  </span>
                  <ChevronDown
                    className={
                      "h-3.5 w-3.5 transition-transform duration-200 " +
                      (isOpen ? "rotate-180" : "")
                    }
                    strokeWidth={2}
                  />
                </button>
                {isOpen && (
                  <ul
                    className="flex flex-col gap-1.5 border-t border-white/[0.06] px-3 py-2"
                    data-testid={`ni2-section-body-${section.key}`}
                  >
                    {section.bullets.map((b, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-[13px] leading-[20px] text-[hsl(var(--nn-ink))]"
                      >
                        <span
                          className="mt-[7px] inline-block h-1 w-1 shrink-0 rounded-full"
                          style={{
                            background: isLimit
                              ? "hsl(var(--nn-warn))"
                              : "hsl(var(--nn-blue))",
                          }}
                        />
                        <span className="min-w-0 flex-1">
                          {renderInlineWithChips(b, onOpenChip)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Visual source summary card. */}
      {layers.length > 0 && (
        <div
          className="rounded-xl border px-3 py-2.5"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.015))",
            borderColor: "rgba(255,255,255,0.10)",
          }}
          data-testid="ni2-source-summary-card"
        >
          <div className="mb-1.5 text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--nn-ink-faint))]">
            Knowledge used
          </div>
          <div className="flex flex-wrap gap-1.5">
            {layers.map((l) => (
              <span
                key={l.key}
                className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px]"
                style={{
                  background: "rgba(160,200,255,0.10)",
                  borderColor: "rgba(160,200,255,0.28)",
                  color: "hsl(var(--nn-ink))",
                }}
                data-testid={`ni2-source-layer-${l.key}`}
              >
                <l.Icon className="h-3 w-3" strokeWidth={1.8} style={{ color: "hsl(var(--nn-blue))" }} />
                {l.label}
                <span className="rounded-full bg-white/5 px-1 text-[9.5px] text-[hsl(var(--nn-ink-faint))]">
                  {l.count}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Suggested follow-up actions. */}
      {followUps.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="ni2-followups">
          {followUps.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onFollowUp && onFollowUp(a)}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11.5px] text-[hsl(var(--nn-ink))] transition-colors hover:border-white/20 hover:bg-white/[0.08]"
              data-testid={`ni2-followup-${a.id}`}
            >
              <a.Icon className="h-3 w-3" strokeWidth={1.8} style={{ color: "hsl(var(--nn-blue))" }} />
              {a.label}
              <ArrowRight className="h-3 w-3 opacity-60" strokeWidth={2} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Helpers exported for the retrieval banner (Ni2LiveBubble uses them).
export const scopeToBannerLabel = (scope, tools = []) => {
  const t = (tools || []).filter((x) => x && !x.denied);
  if (scope === "synthesize") {
    const names = t.map((x) => x.name);
    const hitLayers = [
      names.some((n) => n && n.startsWith("get_personal")) && "My Memory",
      (names.includes("search_department_memory") || names.includes("get_mm_record_by_id")) && "Department",
      (names.includes("search_literature") || names.includes("get_literature_by_ids")) && "Literature",
    ].filter(Boolean);
    return hitLayers.length >= 2
      ? `Retrieved from Multiple Sources · ${hitLayers.join(" + ")}`
      : hitLayers.length === 1
      ? `Retrieved from ${hitLayers[0]}`
      : "Retrieved from Multiple Sources";
  }
  if (scope === "personal") return "Retrieved from My Memory";
  if (scope === "department") return "Retrieved from Department Memory";
  if (scope === "literature") return "Retrieved from Literature";
  return "Retrieved from Repository";
};
